// askSmart-ui/src/pages/TOSP/components/TakeoverWizard.tsx
import React, { useEffect, useState } from "react";
import { formatDate } from "../format";
import { dayBefore } from "../types";

export interface TakeoverResult {
  /** The outgoing tenant's occupancy, and the date it now ends. */
  outgoingOccupancyId: string;
  outgoingEndDate: string;
  /** Where the incoming tenant's lease starts. */
  newCommenceDate: string;
}

interface TakeoverWizardProps {
  isOpen: boolean;
  /** Who currently holds the unit. */
  outgoingTenantName: string;
  outgoingOccupancyId: string;
  /** The incoming tenant, for the summary. Falls back when unnamed. */
  incomingTenantName: string;
  unitLabel: string;
  /**
   * The outgoing lease's other spaces, which end with it.
   *
   * A lease is one agreement and its term runs out as a whole, so taking over
   * one unit ends every space that lease demises. The admin has to be able to
   * see that before agreeing to it — a handover that quietly ended three other
   * spaces would be found out months later.
   */
  alsoEnding?: string[];
  onCancel: () => void;
  onConfirm: (result: TakeoverResult) => void;
}

type Step = "confirm" | "overlap" | "dates" | "summary";

// Assigning a unit somebody else already holds is a handover, not a clash, so
// it's walked through rather than blocked: who's leaving, whether the two
// leases overlap, the dates, and a summary to confirm before anything moves.
export default function TakeoverWizard({
  isOpen,
  outgoingTenantName,
  outgoingOccupancyId,
  incomingTenantName,
  unitLabel,
  alsoEnding = [],
  onCancel,
  onConfirm,
}: TakeoverWizardProps) {
  const [step, setStep] = useState<Step>("confirm");
  const [hasOverlap, setHasOverlap] = useState(false);
  const [takeoverDate, setTakeoverDate] = useState("");
  const [outgoingEndDate, setOutgoingEndDate] = useState("");
  const [newCommenceDate, setNewCommenceDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep("confirm");
    setHasOverlap(false);
    setTakeoverDate("");
    setOutgoingEndDate("");
    setNewCommenceDate("");
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const incoming = incomingTenantName.trim() || "The new tenant";

  // Without an overlap the handover is a single date: the outgoing lease ends
  // the day before the incoming one starts, so the unit is never billed twice.
  const resolved: TakeoverResult = hasOverlap
    ? { outgoingOccupancyId, outgoingEndDate, newCommenceDate }
    : {
        outgoingOccupancyId,
        outgoingEndDate: dayBefore(takeoverDate),
        newCommenceDate: takeoverDate,
      };

  const goToSummary = () => {
    if (hasOverlap) {
      if (!outgoingEndDate || !newCommenceDate) {
        setError("Both dates are required.");
        return;
      }
      if (newCommenceDate > outgoingEndDate) {
        setError(
          "The new lease starts after the existing one ends — go back and choose no overlap.",
        );
        return;
      }
    } else if (!takeoverDate) {
      setError("Takeover Date is required.");
      return;
    }
    setError(null);
    setStep("summary");
  };

  return (
    <div className="smrtdocs-modal-overlay tosp-modal-nested">
      <div className="smrtdocs-modal" style={{ width: "520px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Unit Takeover — {unitLabel}</h2>
        </div>
        <div className="smrtdocs-modal-body">
          {step === "confirm" && (
            <>
              <p className="tosp-wizard-text">
                This Unit is currently occupied by <strong>{outgoingTenantName}</strong>.
              </p>
              {alsoEnding.length > 0 && (
                <p className="tosp-wizard-warning">
                  That lease also covers{" "}
                  <strong>{alsoEnding.join(", ")}</strong>. A lease ends as a whole,
                  so agreeing to this handover ends{" "}
                  {alsoEnding.length === 1 ? "that space" : "those spaces"} too.
                </p>
              )}
              <p className="tosp-wizard-text">Is this Tenant taking over the existing space?</p>
              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="btn-save" onClick={() => setStep("overlap")}>
                  Yes
                </button>
              </div>
            </>
          )}

          {step === "overlap" && (
            <>
              <p className="tosp-wizard-text">
                Will there be an overlap where both tenants are responsible for rent?
              </p>
              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setHasOverlap(false);
                    setStep("dates");
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={() => {
                    setHasOverlap(true);
                    setStep("dates");
                  }}
                >
                  Yes
                </button>
              </div>
            </>
          )}

          {step === "dates" && (
            <>
              {hasOverlap ? (
                <>
                  <p className="tosp-wizard-text">
                    Both occupancies stay active until the existing lease ends.
                  </p>
                  <div className="tosp-form-row">
                    <div className="tosp-field">
                      <label className="tosp-label" htmlFor="tosp-takeover-existing-end">
                        Existing Tenant Lease End Date <span className="tosp-required">*</span>
                      </label>
                      <input
                        id="tosp-takeover-existing-end"
                        type="date"
                        className="manage-input"
                        value={outgoingEndDate}
                        onChange={(e) => setOutgoingEndDate(e.target.value)}
                      />
                    </div>
                    <div className="tosp-field">
                      <label className="tosp-label" htmlFor="tosp-takeover-new-commence">
                        New Tenant Lease Commencement Date{" "}
                        <span className="tosp-required">*</span>
                      </label>
                      <input
                        id="tosp-takeover-new-commence"
                        type="date"
                        className="manage-input"
                        value={newCommenceDate}
                        onChange={(e) => setNewCommenceDate(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="tosp-wizard-text">
                    {outgoingTenantName}'s occupancy ends the day before the takeover date.
                  </p>
                  <div className="tosp-form-row">
                    <div className="tosp-field">
                      <label className="tosp-label" htmlFor="tosp-takeover-date">
                        Takeover Date <span className="tosp-required">*</span>
                      </label>
                      <input
                        id="tosp-takeover-date"
                        type="date"
                        className="manage-input"
                        value={takeoverDate}
                        onChange={(e) => setTakeoverDate(e.target.value)}
                      />
                    </div>
                    <div />
                  </div>
                </>
              )}

              {error && <p className="tosp-field-error">{error}</p>}

              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="btn-cancel" onClick={() => setStep("overlap")}>
                  Back
                </button>
                <button type="button" className="btn-save" onClick={goToSummary}>
                  Continue
                </button>
              </div>
            </>
          )}

          {step === "summary" && (
            <>
              <p className="tosp-wizard-text">Confirm the handover of {unitLabel}:</p>
              <div className="tosp-detail-row">
                <div className="tosp-detail-label">{outgoingTenantName} ends</div>
                <div className="tosp-detail-value">{formatDate(resolved.outgoingEndDate)}</div>
              </div>
              <div className="tosp-detail-row">
                <div className="tosp-detail-label">{incoming} enters</div>
                <div className="tosp-detail-value">{formatDate(resolved.newCommenceDate)}</div>
              </div>
              <div className="tosp-detail-row">
                <div className="tosp-detail-label">Overlap</div>
                <div className="tosp-detail-value">
                  {hasOverlap
                    ? `Both tenants are billed from ${formatDate(resolved.newCommenceDate)} to ${formatDate(resolved.outgoingEndDate)}`
                    : "None — the unit passes over on the takeover date"}
                </div>
              </div>
              <p className="tosp-field-hint" style={{ marginBottom: 16 }}>
                Billing before these dates is unchanged. Nothing is applied until the tenant is
                saved.
              </p>

              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="btn-cancel" onClick={() => setStep("dates")}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={() => onConfirm(resolved)}
                >
                  Confirm Takeover
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
